import { PERIOD_DISPLAY, ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import { formatDollars, formatNumber, formatPercent } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { type FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import ArrowUpRightIcon from 'assets/svg/app/arrow-up-right.svg'
import EyeIcon from 'assets/svg/app/eye.svg'
import InfoIcon from 'assets/svg/app/info-white.svg'
import HelpIcon from 'assets/svg/app/question-mark.svg'
import SNXIcon from 'assets/svg/partners/snx.svg'
import Button from 'components/Button'
import { BaseCard } from 'components/Card/style'
import ColoredPrice from 'components/ColoredPrice'
import { LabelPill } from 'components/Pill'
import { Body, Heading } from 'components/Text'
import Tooltip from 'components/Tooltip/TextTooltip'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { EXTERNAL_LINKS } from 'constants/links'
import { DASHBOARD_SECTION_HEIGHT_PX, FEEREBATE_DISABLED } from 'constants/ui'
import { SectionSeparator } from 'sections/futures/mobile'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectIsolatedActivePositionsMargin,
	selectSelectedPortfolioTimeframe,
} from 'state/futures/isolatedMargin/selectors'
import { selectAvailableMargin, selectTotalUnrealizedPnl } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectSnxRewards } from 'state/staking/selectors'
import { selectSignerWallet } from 'state/wallet/selectors'
import media from 'styles/media'
import { providerIsCrossMargin } from 'utils/futures'

import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import { DrawerTab, WithdrawTab } from 'sections/accounts/constants'
import AccountHealth from './AccountHealth'
type Props = {
	maskAsSensitive: boolean
	balanceChange?: number
	balanceChangePercent?: number
	toggleMaskAsSensitive: () => void
}

const EstRewardsTooltip: FC<{ content: string }> = memo(({ content }) => {
	return (
		<Tooltip
			height="auto"
			preset="bottom"
			width="240px"
			content={content}
			style={{ textTransform: 'none', left: '30px' }}
		>
			<StyledHelpIcon />
		</Tooltip>
	)
})

const AccountBalance: FC<Props> = memo(
	({ maskAsSensitive, balanceChange = 0, balanceChangePercent = 0, toggleMaskAsSensitive }) => {
		const { t } = useTranslation()
		const { trackWithdrawFlow } = useAnalytics()
		const dispatch = useAppDispatch()
		const wallet = useAppSelector(selectSignerWallet)
		const availableMargin = useAppSelector(selectAvailableMargin)
		const perpsProvider = useAppSelector(selectPerpsProvider)
		const remainingMargin = useAppSelector(selectIsolatedActivePositionsMargin)
		const upnl = useAppSelector(selectTotalUnrealizedPnl)
		const snxRewards = useAppSelector(selectSnxRewards)
		const timeFrame = useAppSelector(selectSelectedPortfolioTimeframe)

		const changeLabel = useMemo(() => {
			if (perpsProvider === PerpsProvider.SNX_V3_BASE) {
				return `${PERIOD_DISPLAY[timeFrame]} PNL`
			}
			return formatPercent(balanceChangePercent ?? ZERO_WEI, {
				maxDecimals: 2,
			})
		}, [perpsProvider, balanceChangePercent, timeFrame])

		const balance = useMemo(() => {
			if (providerIsCrossMargin(perpsProvider)) {
				return availableMargin
			}

			return wei(remainingMargin).add(availableMargin)
		}, [availableMargin, remainingMargin, perpsProvider])

		const showFeeRebate = useMemo(
			() => wei(snxRewards).gt(ZERO_WEI) && !FEEREBATE_DISABLED,
			[snxRewards]
		)

		return (
			<AccountBalanceContainer>
				<FlexDivCol>
					<HeadingContainer>
						<Body weight="semiBold" color="secondary">
							{t('dashboard.overview.account-balances.title')}
						</Body>
						<LabelPill $color="gray" $size="small" $weight="bold">
							<StyledInfoIcon />
							{t(
								`dashboard.overview.account-balances.${
									providerIsCrossMargin(perpsProvider) ? 'cross-margin' : 'isolated-margin'
								}`
							)}
						</LabelPill>
					</HeadingContainer>
					<StyledSectionSeparator />
					<FlexDivCol rowGap="5px">
						<FlexDivRowCentered justifyContent="flex-start" columnGap="10px">
							<StyledHeading variant="h3">
								{formatDollars(balance, { maskAsSensitive, floor: true })}
							</StyledHeading>
							<StyledEyeIcon onClick={toggleMaskAsSensitive} />
						</FlexDivRowCentered>
						<FlexDivRowCentered justifyContent="flex-start" columnGap="10px">
							<ColoredPrice priceChange={balanceChange >= 0 ? 'up' : 'down'}>
								{formatDollars(balanceChange, { maskAsSensitive })}
							</ColoredPrice>
							<LabelPill
								$color={balanceChange > 0 ? 'green' : 'red'}
								$size="small"
								$weight="semiBold"
							>
								{changeLabel}
							</LabelPill>
						</FlexDivRowCentered>
					</FlexDivCol>
				</FlexDivCol>
				<AccountHealth
					maskAsSensitive={maskAsSensitive}
					allocatedMargin={remainingMargin}
					availableMargin={availableMargin}
					upnl={upnl}
				/>
				<FlexDivCol rowGap="10px">
					{providerIsCrossMargin(perpsProvider) && showFeeRebate && (
						<>
							<FlexDivRowCentered>
								<FlexDivRowCentered justifyContent="space-between">
									<Body color="secondary">Est. Rewards:</Body>
									<EstRewardsTooltip
										content={t('dashboard.overview.account-balances.est-rewards-tooltip')}
									/>
								</FlexDivRowCentered>
								<FlexDivRowCentered columnGap="5px">
									<Body color="primary" mono>
										{formatNumber(snxRewards, { suggestDecimals: true })}
									</Body>
									<SNXIcon height={7} width={11} />
								</FlexDivRowCentered>
							</FlexDivRowCentered>
							<RewardsInfoContainer
								justifyContent="center"
								onClick={() =>
									window.open(EXTERNAL_LINKS.Docs.SnxTradingIncentivesOnBase, '_blank')
								}
							>
								<Body color="preview" fontSize={11}>
									{t('dashboard.overview.account-balances.how-to-earn')}
								</Body>
								<ArrowUpRightIcon height={16} width={13} />
							</RewardsInfoContainer>
						</>
					)}
					<FlexDivRow columnGap="10px">
						<Button
							size="small"
							fullWidth
							onClick={() => {
								trackWithdrawFlow(EVENT_NAME.CLICK_TRANSFER_WITHDRAW)
								dispatch(
									setOpenModalWithParams({
										type: 'manage_wallet',
										params: { tab: DrawerTab.WITHDRAW, innerTab: WithdrawTab.INTERNAL },
									})
								)
							}}
							disabled={!wallet}
						>
							{t('dashboard.overview.account-balances.transfer')}
						</Button>
					</FlexDivRow>
				</FlexDivCol>
			</AccountBalanceContainer>
		)
	}
)

const StyledHelpIcon = styled(HelpIcon)`
	display: flex;
	margin-left: 4px;
`

const RewardsInfoContainer = styled(FlexDivRowCentered)`
	padding: 5px;
	border-radius: 8px;
	background: #1d1a15;
	cursor: pointer;
	svg {
		path {
			stroke: #ffb800;
		}
	}
`

const StyledSectionSeparator = styled(SectionSeparator)`
	margin: 0 0 10px 0;
`

const StyledInfoIcon = styled(InfoIcon)`
	margin-right: 5px;
`

const AccountBalanceContainer = styled(BaseCard)`
	flex: 3;
	border-radius: 8px;
	min-width: 300px;
	max-width: 340px;
	height: ${DASHBOARD_SECTION_HEIGHT_PX};
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 20px;
	gap: 20px;

	${media.lessThan('xl')`
		max-width: unset;
	`}

	${media.lessThan('md')`
		grid-row-start: 2;
		grid-column: span 2;
	`}
`

const StyledEyeIcon = styled(EyeIcon)`
	cursor: pointer;
`

const HeadingContainer = styled(FlexDivRowCentered)`
	padding: 0 0 10px;
`

const StyledHeading = styled(Heading)`
	font-weight: 700;
	font-feature-settings: 'tnum';
`

export default AccountBalance
